import intersection from 'lodash/intersection';
import without from 'lodash/without';
import uniq from 'lodash/uniq';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import composeGlobalFailureMessaging from 'app/helpers/messaging/composeGlobalFailureMessaging';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';

const BIB_ISBN_NAVIGATE_KEY = 'bib-isbn-navigate';

export const SUPPORTED_ENRICHMENT_IDS = {
  AUDIO_VIDEO: 'audiovideo',
  AUTHOR: 'author',
  AWARDS: 'awards',
  GAME: 'game',
  LOOK_INSIDE: 'lookinside',
  METRICS: 'altmetrics',
  PROFILE: 'bookprofile',
  REVIEWS: 'reviews',
  RESOURCES_FOR_COLLEGE_LIBRARIES: 'rcl',
  READING_LEVEL: 'readinglevel',
  SERIES: 'series',
  SIMILAR: 'similar',
  SUMMARY: 'summary',
  TAGS: 'tags',
  SHELF_BROWSE: 'shelfbrowse'
};

const beforeIsbnPageNavigate = () => {
  const record = {
    fromUrl: window.location.href,
    timestamp: Date.now()
  };

  window.localStorage.setItem(BIB_ISBN_NAVIGATE_KEY, JSON.stringify(record));
};

const libraryThingConnectorAdapter = {
  getAvailableEnrichmentIds: ({ excludeIds = [] } = {}) => {
    const enabledEnrichmentIds = uniq(window.LibraryThingConnector.enrichmentsOn() || []);

    return without(intersection(enabledEnrichmentIds, Object.values(SUPPORTED_ENRICHMENT_IDS)), ...excludeIds);
  },
  isEnrichmentShown: enrichmentId => {
    return window.LibraryThingConnector.isEnrichmentShown(enrichmentId);
  },
  populate: ({ isbn, upc }) => {
    window.LibraryThingConnector.runUnboundWithMetadata({
      isbn,
      unbound_run_link_function: destination => {
        const url = new URL(destination);
        const query = url.searchParams.get('query');
        const matches = /^ISBN:(\d+)$/.exec(query);
        const bibIsbn = matches?.[1];

        if (bibIsbn) {
          beforeIsbnPageNavigate();
        }

        window.location = bibIsbn
          ? new URL(`/item/show_by_isbn/${bibIsbn}`, window.location.origin).toString()
          : destination;
      },
      upc
    });
  }
};

const makeScriptUrl = (accountId, instanceId) => {
  const queryParams = new URLSearchParams({ a_id: accountId });

  if (instanceId) {
    queryParams.append('i_id', instanceId);
  }

  return `https://unbound.syndetics.com/syndeticsunbound/connector/initiator.php?${queryParams.toString()}`;
};

let onLoadedPromiseInstance = null;
const getOnLoadedPromiseInstance = (accountId, instanceId) => {
  if (__SERVER__) {
    return Promise.resolve(null);
  }

  if (onLoadedPromiseInstance) {
    return onLoadedPromiseInstance;
  }

  onLoadedPromiseInstance = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = makeScriptUrl(accountId, instanceId);
    script.async = true;
    script.onload = () => {
      if (!window?.LibraryThingConnector) {
        reject(new Error('Failed to load SyndeticsUnbound LibraryThingConnector'));
      }

      resolve(libraryThingConnectorAdapter);
    };

    document.body.appendChild(script);
  });

  return onLoadedPromiseInstance;
};

const useSyndeticsUnbound = onLoaded => {
  const dispatch = useDispatch();
  const currentLibrary = useSelector(selectCurrentLibrary);

  const isEnabled = currentLibrary.getIn(['syndeticsUnbound', 'isEnabled']) ?? false;
  const accountId = currentLibrary.getIn(['syndeticsUnbound', 'accountId']);
  const instanceId = currentLibrary.getIn(['syndeticsUnbound', 'instanceId']);

  useEffect(() => {
    if (!(isEnabled && accountId && onLoaded)) {
      return;
    }

    getOnLoadedPromiseInstance(accountId, instanceId).then(onLoaded);
  }, [accountId, instanceId, isEnabled, onLoaded]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const record = JSON.parse(window.localStorage.getItem(BIB_ISBN_NAVIGATE_KEY) || null);
    if (!record) {
      return;
    }

    const MAX_REDIRECT_DURATION_MS = 10 * 1000;
    const wasRedirectedFromFromFailedIsbn =
      record?.fromUrl === window.location.href &&
      Math.max(Date.now() - record?.timestamp, 0) < MAX_REDIRECT_DURATION_MS;

    if (wasRedirectedFromFromFailedIsbn) {
      dispatch({
        messaging: composeGlobalFailureMessaging(
          {
            classification: 'TitleNotInCollection'
          },
          {
            useFormattedMessage: true
          }
        ),
        type: 'ONLY_FOR_MESSAGING'
      });
    }
  }, [dispatch, isEnabled]);
};

export default useSyndeticsUnbound;
